// Fase 17.3, Plano 04 — "Importar roteiros do CargaMáquina": upload → prévia (sem gravar) → correção dos códigos de célula
// → confirmação → relatório de exceções (CSV) → histórico. O plano é sempre recalculado no servidor ao confirmar.
import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  CelulaCodigoOverrideDto,
  ExcecaoImportacaoRoteiroDto,
  ImportacaoRoteiroResumoDto,
  MotivoExcecaoImportacaoRoteiro,
  PreviaImportacaoCargaMaquinaDto,
  ResultadoImportacaoCargaMaquinaDto,
} from '@pieltec/contracts';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { CampoSelect } from '../../../components/ui/campo-select';
import { Input } from '../../../components/ui/input';
import {
  baixarTexto,
  celulaEditavel,
  codigoCelulaValido,
  codigoEditado,
  confirmarImportacaoCargaMaquina,
  excecoesDaImportacao,
  excecoesParaCsv,
  lerArquivo,
  listarImportacoes,
  overridesAlterados,
  previaImportacaoCargaMaquina,
  previaTemNovidade,
  temCodigoInvalido,
  ROTULO_MOTIVO_EXCECAO,
  type ArquivoLido,
} from '../../../lib/roteiros/importacao-cargamaquina';
import { SeletorArquivo } from './seletor-arquivo';

const TODOS_OS_MOTIVOS = '';
const OPCOES_MOTIVO = [
  { value: TODOS_OS_MOTIVOS, label: 'Todos os motivos' },
  ...(Object.entries(ROTULO_MOTIVO_EXCECAO) as [MotivoExcecaoImportacaoRoteiro, string][]).map(([value, label]) => ({ value, label })),
];

function Totais({ rotulo, valor }: { readonly rotulo: string; readonly valor: number }) {
  return (
    <div className="flex flex-col gap-1 rounded-md border p-4">
      <span className="text-sm text-muted-foreground">{rotulo}</span>
      <span className="text-[28px] font-semibold leading-tight">{valor}</span>
    </div>
  );
}

function Cabecalho({ colunas }: { readonly colunas: readonly string[] }) {
  return (
    <thead>
      <tr className="text-left">{colunas.map((coluna) => <th key={coluna} className="py-2 pr-4 font-semibold">{coluna}</th>)}</tr>
    </thead>
  );
}

function nomeArquivoCsv(prefixo: string, origem: string): string {
  const base = origem.replace(/\.[^.]+$/, '').replace(/[^\w.-]+/g, '-');
  return `${prefixo}-${base || 'importacao'}.csv`;
}

function formatarData(iso: string): string {
  const data = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T'));
  return Number.isNaN(data.getTime()) ? iso : data.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

export function ImportarCargaMaquinaPage() {
  const [arquivo, setArquivo] = useState<File | null>(null);
  // Nome + conteúdo do arquivo da prévia, lidos juntos: a confirmação usa exatamente o que a prévia mostrou (P3-WR-03).
  const [lido, setLido] = useState<ArquivoLido | null>(null);
  const [previa, setPrevia] = useState<PreviaImportacaoCargaMaquinaDto | null>(null);
  const [overridesAtivos, setOverridesAtivos] = useState<Readonly<Record<string, string>>>({});
  const [edicoes, setEdicoes] = useState<Readonly<Record<string, string>>>({});
  const [filtroMotivo, setFiltroMotivo] = useState(TODOS_OS_MOTIVOS);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [confirmando, setConfirmando] = useState(false);
  const [resultado, setResultado] = useState<ResultadoImportacaoCargaMaquinaDto | null>(null);
  const [historico, setHistorico] = useState<ImportacaoRoteiroResumoDto[]>([]);
  const [erroHistorico, setErroHistorico] = useState<string | null>(null);

  const carregarHistorico = useCallback(async (): Promise<void> => {
    try {
      setHistorico(await listarImportacoes());
      setErroHistorico(null);
    } catch {
      setErroHistorico('Não foi possível carregar as importações anteriores.');
    }
  }, []);

  useEffect(() => { void carregarHistorico(); }, [carregarHistorico]);

  function escolher(escolhido: File | null): void {
    setArquivo(escolhido);
    setLido(null);
    setPrevia(null);
    setResultado(null);
    setOverridesAtivos({});
    setEdicoes({});
    setErro(null);
  }

  const alterados = useMemo(() => (previa ? overridesAlterados(previa.celulas, edicoes) : []), [previa, edicoes]);
  const desatualizada = alterados.length > 0;
  const codigosInvalidos = useMemo(() => temCodigoInvalido(previa?.celulas ?? [], edicoes), [previa, edicoes]);

  function corpoComOverrides(base64: string, nome: string, extras: readonly CelulaCodigoOverrideDto[]): { arquivoNome: string; arquivoBase64: string; celulasCodigo: CelulaCodigoOverrideDto[] } {
    const mapa = new Map(Object.entries(overridesAtivos));
    for (const item of extras) mapa.set(item.nomeArquivo, item.codigo);
    return { arquivoNome: nome, arquivoBase64: base64, celulasCodigo: [...mapa].map(([nomeArquivo, codigo]) => ({ nomeArquivo, codigo })) };
  }

  async function gerarPrevia(): Promise<void> {
    if (!arquivo || codigosInvalidos) return;
    setOcupado(true);
    setErro(null);
    setResultado(null);
    try {
      const lidoAgora = lido ?? await lerArquivo(arquivo);
      const corpo = corpoComOverrides(lidoAgora.base64, lidoAgora.nome, alterados);
      const nova = await previaImportacaoCargaMaquina(corpo);
      setLido(lidoAgora);
      setOverridesAtivos(Object.fromEntries(corpo.celulasCodigo.map((item) => [item.nomeArquivo, item.codigo])));
      setPrevia(nova);
      setEdicoes(Object.fromEntries(nova.celulas.map((celula) => [celula.nomeArquivo, celula.codigo])));
      setFiltroMotivo(TODOS_OS_MOTIVOS);
    } catch (falha) {
      setPrevia(null);
      setErro(falha instanceof Error ? falha.message : 'Não foi possível gerar a prévia.');
    } finally {
      setOcupado(false);
    }
  }

  async function confirmar(): Promise<void> {
    if (!lido || !previa) return;
    setConfirmando(false);
    setOcupado(true);
    setErro(null);
    try {
      const corpo = corpoComOverrides(lido.base64, lido.nome, []);
      setResultado(await confirmarImportacaoCargaMaquina(corpo));
      setPrevia(null);
      setEdicoes({});
      setOverridesAtivos({});
      await carregarHistorico();
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'Não foi possível confirmar a importação.');
    } finally {
      setOcupado(false);
    }
  }

  async function baixarExcecoesDaImportacao(item: ImportacaoRoteiroResumoDto): Promise<void> {
    try {
      baixarTexto(nomeArquivoCsv('excecoes', item.arquivoNome), excecoesParaCsv(await excecoesDaImportacao(item.id)));
    } catch {
      setErroHistorico('Não foi possível baixar as exceções desta importação.');
    }
  }

  const excecoesVisiveis: readonly ExcecaoImportacaoRoteiroDto[] = previa
    ? previa.excecoes.filter((excecao) => filtroMotivo === TODOS_OS_MOTIVOS || excecao.motivo === filtroMotivo)
    : [];
  const temNovidade = previa !== null && previaTemNovidade(previa);
  const podeConfirmar = previa !== null && !desatualizada && !codigosInvalidos && !ocupado && temNovidade;

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 p-6">
      <Card>
        <CardContent className="flex flex-col gap-4">
          <h1 className="text-[28px] font-semibold leading-tight">Importar roteiros do CargaMáquina</h1>
          <p className="text-sm text-muted-foreground">
            Envie o CSV de materiais exportado do CargaMáquina (com Serviço e Células, ou a lista de materiais com Descrição para sugerir cores). A prévia mostra o que será criado e as exceções; nada é gravado até você confirmar.
            Reenviar o arquivo só acrescenta o que é novo: nada que o PCP ajustou é alterado.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <SeletorArquivo aceita=".csv" rotulo="Arquivo CSV do CargaMáquina" desabilitado={ocupado} onEscolher={escolher} />
            <Button type="button" disabled={!arquivo || ocupado || codigosInvalidos} onClick={() => void gerarPrevia()}>
              Gerar prévia
            </Button>
          </div>
          {erro ? <p role="alert" className="text-sm text-destructive">{erro}</p> : null}
        </CardContent>
      </Card>

      {resultado ? (
        <Card>
          <CardContent className="flex flex-col gap-4">
            <h2 className="text-sm font-semibold">Importação concluída</h2>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-6">
              <Totais rotulo="Peças importadas" valor={resultado.pecasImportadas} />
              <Totais rotulo="Já existentes" valor={resultado.pecasJaExistentes} />
              <Totais rotulo="Modelos criados" valor={resultado.modelosCriados} />
              <Totais rotulo="Células criadas" valor={resultado.celulasCriadas} />
              <Totais rotulo="Códigos legados" valor={resultado.codigosLegadosCriados} />
              <Totais rotulo="Cores sugeridas" valor={resultado.coresSugeridas} />
              <Totais rotulo="Tintas da peça ligadas" valor={resultado.tintasDaPecaVinculadas} />
            </div>
            <p className="text-sm text-muted-foreground">
              {resultado.excecoes.length} exceção(ões) registrada(s) nesta importação; baixe o relatório em “Importações anteriores”.
            </p>
            <a href="/pcp/roteiros" className="text-sm underline">Ver peças</a>
          </CardContent>
        </Card>
      ) : null}

      {previa ? (
        <>
          <Card>
            <CardContent className="flex flex-col gap-4">
              <div className="grid grid-cols-2 gap-4 md:grid-cols-6">
                <Totais rotulo="Peças no arquivo" valor={previa.pecas.noArquivo} />
                <Totais rotulo="Novas" valor={previa.pecas.novas} />
                <Totais rotulo="Já existem" valor={previa.pecas.jaExistem} />
                <Totais rotulo="Divergentes" valor={previa.pecas.divergentes} />
                <Totais rotulo="Sem material" valor={previa.pecas.semMaterial} />
                <Totais rotulo="Com exceção" valor={previa.pecas.comExcecaoBloqueante} />
              </div>
              {desatualizada ? <p role="status" className="text-sm font-semibold text-destructive">Prévia desatualizada: gere de novo</p> : null}
              <div className="flex flex-wrap items-center gap-4">
                <Button type="button" disabled={!podeConfirmar} onClick={() => setConfirmando(true)}>Confirmar importação</Button>
                {!temNovidade ? <span className="text-sm text-muted-foreground">Nada novo para importar neste arquivo.</span> : null}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex flex-col gap-4">
              <h2 className="text-sm font-semibold">Tintas da peça</h2>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
                <Totais rotulo="Ligadas automaticamente" valor={previa.tintasDaPeca.vinculadas} />
                <Totais rotulo="Já definidas (não mudam)" valor={previa.tintasDaPeca.jaDefinidas} />
                <Totais rotulo="Mais de uma tinta citada" valor={previa.tintasDaPeca.comMaisDeUmaTinta} />
                <Totais rotulo="Tinta citada inativa ou incompleta" valor={previa.tintasDaPeca.tintaCitadaInelegivel} />
                <Totais rotulo="Sem código de tinta" valor={previa.tintasDaPeca.semTintaCitada} />
              </div>
              <p className="text-sm text-muted-foreground">A peça é ligada à tinta cujo código abre a linha “COR:” da descrição, quando só uma tinta é citada. Vínculos existentes e escolhas do PCP nunca mudam; as exceções ficam na lista abaixo e no relatório.</p>
              <a href="/pcp/roteiros?semTintaDaPeca=true" className="text-sm underline">Ver peças pintadas sem tinta da peça</a>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex flex-col gap-4">
              <h2 className="text-sm font-semibold">Cores sugeridas</h2>
              <Totais rotulo="Cores sugeridas" valor={previa.coresSugeridas} />
              <p className="text-sm text-muted-foreground">
                {previa.modo === 'DESCRICOES' ? 'Lista de materiais com descrição: só as cores são sugeridas, nenhum roteiro é criado. ' : ''}
                A cor vem da descrição do material (código RAL, linha “COR:” ou TEFLON) e só vale para peças ainda sem cor. O PCP confirma ou ignora cada uma na ficha da peça; nada é gravado nos dados técnicos.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex flex-col gap-4">
              <h2 className="text-sm font-semibold">Serviços</h2>
              <table className="w-full text-sm">
                <Cabecalho colunas={['Código', 'Nome', 'Situação']} />
                <tbody>
                  {previa.servicos.map((servico) => (
                    <tr key={servico.codigo}><td className="py-1 pr-4">{servico.codigo}</td><td className="py-1 pr-4">{servico.nome}</td><td className="py-1 pr-4">{servico.situacao === 'NOVO' ? 'Novo' : 'Já existe'}</td></tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex flex-col gap-4">
              <h2 className="text-sm font-semibold">Células</h2>
              <p className="text-sm text-muted-foreground">
                Corrija o código proposto quando o do CargaMáquina for diferente (por exemplo, Pintura Líquida e PTFE G1, G2, G4 ou as cabines pó E e F). Células que já existem mantêm o código.
              </p>
              <table className="w-full text-sm">
                <Cabecalho colunas={['Nome no arquivo', 'Código', 'Serviço', 'Situação']} />
                <tbody>
                  {previa.celulas.map((celula) => {
                    const valor = edicoes[celula.nomeArquivo] ?? celula.codigo;
                    const existente = !celulaEditavel(celula);
                    const invalido = !existente && !codigoCelulaValido(codigoEditado(celula, edicoes));
                    return (
                      <tr key={celula.nomeArquivo}>
                        <td className="py-1 pr-4">{celula.nomeArquivo}</td>
                        <td className="py-1 pr-4">
                          <Input
                            aria-label={`Código da célula ${celula.nomeArquivo}`}
                            aria-invalid={invalido}
                            className="w-32 uppercase"
                            value={valor}
                            disabled={existente || ocupado}
                            maxLength={20}
                            onChange={(evento) => setEdicoes((atual) => ({ ...atual, [celula.nomeArquivo]: evento.target.value }))}
                          />
                          {invalido ? <p role="alert" className="pt-1 text-xs text-destructive">Código inválido</p> : null}
                        </td>
                        <td className="py-1 pr-4">{celula.servicoCodigo}</td>
                        <td className="py-1 pr-4">{existente ? 'Já existe' : 'Nova'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex flex-col gap-4">
              <h2 className="text-sm font-semibold">Modelos de roteiro</h2>
              <table className="w-full text-sm">
                <Cabecalho colunas={['Código', 'Sequência', 'Peças', 'Situação']} />
                <tbody>
                  {previa.modelos.map((modelo) => (
                    <tr key={modelo.assinatura}><td className="py-1 pr-4">{modelo.codigo}</td><td className="py-1 pr-4">{modelo.nome}</td><td className="py-1 pr-4">{modelo.pecas}</td><td className="py-1 pr-4">{modelo.situacao === 'NOVO' ? 'Novo' : 'Já existe'}</td></tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex flex-col gap-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <h2 className="text-sm font-semibold">Exceções ({previa.excecoes.length})</h2>
                <div className="flex flex-wrap items-center gap-4">
                  <CampoSelect
                    aria-label="Filtrar exceções por motivo"
                    className="w-64"
                    value={filtroMotivo}
                    onValueChange={setFiltroMotivo}
                    options={OPCOES_MOTIVO}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    disabled={previa.excecoes.length === 0}
                    onClick={() => baixarTexto(nomeArquivoCsv('excecoes', previa.arquivoNome), excecoesParaCsv(previa.excecoes))}
                  >
                    Baixar exceções (CSV)
                  </Button>
                </div>
              </div>
              <table className="w-full text-sm">
                <Cabecalho colunas={['Motivo', 'Peça', 'Detalhe', 'Bloqueia']} />
                <tbody>
                  {excecoesVisiveis.map((excecao, indice) => (
                    <tr key={`${excecao.codigoBase}-${excecao.motivo}-${indice}`}>
                      <td className="py-1 pr-4">{ROTULO_MOTIVO_EXCECAO[excecao.motivo]}</td>
                      <td className="py-1 pr-4">{excecao.codigoBase}</td>
                      <td className="py-1 pr-4">{excecao.detalhe}</td>
                      <td className="py-1 pr-4">{excecao.bloqueante ? 'Sim' : 'Não'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          <AlertDialog open={confirmando} onOpenChange={setConfirmando}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Confirmar importação?</AlertDialogTitle>
                <AlertDialogDescription>
                  Serão criados {previa.pecas.novas} roteiro(s) de peça, {previa.modelos.filter((modelo) => modelo.situacao === 'NOVO').length} modelo(s),{' '}
                  {previa.celulas.filter((celula) => celula.situacao !== 'EXISTENTE').length} célula(s), {previa.coresSugeridas} cor(es) sugerida(s), {previa.tintasDaPeca.vinculadas} tinta(s) da peça ligada(s) e{' '}
                  {previa.servicos.filter((servico) => servico.situacao === 'NOVO').length} serviço(s). {previa.excecoes.length} exceção(ões) ficam registradas e
                  essas peças não serão importadas agora. Roteiros que já existem não são alterados.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Voltar</AlertDialogCancel>
                <AlertDialogAction onClick={() => void confirmar()}>Importar agora</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      ) : null}

      <Card>
        <CardContent className="flex flex-col gap-4">
          <h2 className="text-sm font-semibold">Importações anteriores</h2>
          {erroHistorico ? <p role="alert" className="text-sm text-destructive">{erroHistorico}</p> : null}
          {historico.length === 0 && !erroHistorico ? <p className="text-sm text-muted-foreground">Nenhuma importação registrada.</p> : null}
          {historico.length > 0 ? (
            <table className="w-full text-sm">
              <Cabecalho colunas={['Data', 'Arquivo', 'Usuário', 'Peças importadas', 'Exceções', '']} />
              <tbody>
                {historico.map((item) => (
                  <tr key={item.id}>
                    <td className="py-1 pr-4">{formatarData(item.createdAt)}</td>
                    <td className="py-1 pr-4">{item.arquivoNome}</td>
                    <td className="py-1 pr-4">{item.actorNome ?? '—'}</td>
                    <td className="py-1 pr-4">{item.resumo.pecasImportadas ?? 0}</td>
                    <td className="py-1 pr-4">{item.resumo.excecoes ?? 0}</td>
                    <td className="py-1 pr-4">
                      <Button type="button" variant="outline" size="sm" onClick={() => void baixarExcecoesDaImportacao(item)}>Baixar exceções</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
